import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useLoad, act } from "@/lib/hooks";
import { api } from "@/lib/api";
import { useAccess, useAuth } from "@/context/AuthContext";
import { PageHeader, Section, StatusBadge, NativeSelect, Field } from "@/components/common";
import { PageSearch } from "@/components/PageSearch";

const ROLES = [{ value: "tenant_admin", label: "Tenant admin" }, { value: "accountant", label: "Accountant" }, { value: "sales", label: "Sales" }, { value: "production", label: "Production / operator" }, { value: "purchasing", label: "Purchasing" }, { value: "viewer", label: "Viewer" }, { value: "hr_manager", label: "HR manager" }];

export default function Users() {
  const [rows, reload] = useLoad("/tenant/users");
  const [br] = useLoad("/tenant/branches");
  const branches = br?.branches || [];
  const bname = Object.fromEntries(branches.map((b) => [b.id, b.name]));
  const [form, setForm] = useState(null);
  const { canWrite } = useAccess();
  const { user } = useAuth();
  const can = canWrite(["tenant_admin"]);
  const limit = user?.tenant?.subscription?.entitlements?.users;

  const save = async () => { if (await act(() => api.post("/tenant/users", form), "User created")) { setForm(null); reload(); } };
  const update = async (id, body) => { if (await act(() => api.put(`/tenant/users/${id}`, body), "User updated")) reload(); };

  return (
    <>
      <PageHeader title="Users" subtitle={`Staff accounts · ${rows?.filter((u) => u.active).length ?? 0} of ${limit ?? "—"} seats used`}>
        <PageSearch testId="users-search" placeholder="Search name or email" />
        {can && <Button data-testid="new-user-button" onClick={() => setForm({ name: "", email: "", password: "", role: "sales" })} className="bg-[#45BFB4] text-[#13294B] hover:bg-[#389F95] hover:text-white"><Plus className="mr-1 h-4 w-4" />Add user</Button>}
      </PageHeader>
      <Section>
        <div className="overflow-x-auto"><table className="po-table" data-testid="users-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Branches</th><th>Branch restriction</th><th>Status</th><th></th></tr></thead>
          <tbody>{rows?.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.email}`}>
              <td className="font-medium text-slate-900">{u.name}</td><td>{u.email}</td>
              <td className="w-44">{can && u.id !== user.id ? <NativeSelect testId={`user-role-${u.id}`} value={u.role} onChange={(v) => update(u.id, { role: v })} options={ROLES} /> : <span className="capitalize">{u.role.replace("_", " ")}</span>}</td>
              <td className="min-w-[200px]"><div className="flex flex-wrap gap-1" data-testid={`user-branches-${u.id}`}>{branches.map((b) => {
                const on = (u.branch_ids || []).includes(b.id);
                return <button key={b.id} disabled={!can} data-testid={`user-branch-${u.id}-${b.id}`} onClick={() => update(u.id, { branch_ids: on ? u.branch_ids.filter((x) => x !== b.id) : [...(u.branch_ids || []), b.id] })}
                  className={`rounded-full border px-2 py-0.5 text-[11px] ${on ? "border-[#13294B] bg-[#13294B] text-white" : "border-slate-200 text-slate-500"}`}>{bname[b.id]}</button>;
              })}</div></td>
              <td><label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid={`user-restrict-${u.id}`} disabled={!can || u.id === user.id} checked={!!u.branch_restricted} onChange={(e) => update(u.id, { branch_restricted: e.target.checked })} />Only assigned branches</label></td>
              <td><StatusBadge status={u.active ? "active" : "canceled"} /></td>
              <td className="text-right">{can && u.id !== user.id && <Button size="sm" variant="ghost" data-testid={`user-toggle-${u.id}`} onClick={() => update(u.id, { active: !u.active })}>{u.active ? "Deactivate" : "Activate"}</Button>}</td>
            </tr>
          ))}</tbody>
        </table></div>
      </Section>
      <Dialog open={!!form} onOpenChange={() => setForm(null)}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>Add user</DialogTitle><DialogDescription className="sr-only">Create a workspace user account and choose its role.</DialogDescription></DialogHeader>
          {form && <div className="space-y-3">
            <Field label="Name"><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Email"><Input data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Temporary password (min 8 chars)"><PasswordInput data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
            <Field label="Role"><NativeSelect testId="user-role-select" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={ROLES} /></Field>
          </div>}
          <DialogFooter><Button data-testid="user-save-button" onClick={save}>Create user</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
